// Today.jsx — the Pronto morning home (redesign of Daily).
//
// Fidelity pass against docs/pronto-prototype-spec.md ("## Today"): a weather
// header + a per-view-mode body, in the Glass system. Same data as the legacy
// Daily screen (weather, active-bag brew preview, most-urgent expiring item,
// tonight suggestion, shop count) — only PRESENTATION changed; all logic is
// reused from lib/* and computeMorningBrew.
//
// View modes (docs/pronto-redesign.md §6, prototype `homeAI/isHybrid/isTraditional`):
//   adaptive (default) / ai — share the "◇ PRONTO SUGGESTS" layout (rate-yesterday
//                             card → Ask field → Morning briefing)
//   hybrid                  — an "Oggi:" strip + a 2×2 action grid
//   traditional             — a 2×2 action grid + a passive Ask row
import { useEffect, useState, useMemo } from "react";
import { ChevronRight } from "lucide-react";
import { GlassCard, Sheet } from "../components/pronto";
import { getWeather, timeOfDay, brewSuggestionFromWeather } from "../lib/weather";
import { useSoonItems } from "../lib/pantry";
import { canisterBean, findActiveBags, forecastDepletion } from "../lib/brew-helpers";
import { computeMorningBrew } from "../components/MorningBrew";
import { greeting as i18nGreeting, normalizeLocale, normalizeViewMode } from "../lib/i18n";
import { loadUsage, rankMetrics, PHASE_LABEL } from "../lib/usage";
import { listPlayers, playEverywhere } from "../lib/sonos";

export function Today({ store, onNavigate }) {
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => { try { const w = await getWeather(); if (!cancelled) setWeather(w); } catch {} };
    load();
    const id = setInterval(load, 20 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const locale = normalizeLocale(store.preferences?.language);
  const viewMode = normalizeViewMode(store.preferences?.view_mode);

  // Adaptive signal: current phase + this user's recent usage (for card ranking).
  const phase = timeOfDay();
  const [usage, setUsage] = useState([]);
  useEffect(() => {
    let cancelled = false;
    loadUsage(store.userId).then(e => { if (!cancelled) setUsage(e); });
    return () => { cancelled = true; };
  }, [store.userId]);

  // --- Data (mirrors Daily) ---------------------------------------------------
  const hour = new Date().getHours();
  const greeting = i18nGreeting(hour, locale);
  const dayName = new Date().toLocaleDateString(locale === "it" ? "it-IT" : "en-US", { weekday: "long" });

  const brewSugg = useMemo(() => brewSuggestionFromWeather(weather), [weather]);
  const expiring = useMemo(() => useSoonItems(store.inventory, store.skus).slice(0, 3), [store.inventory, store.skus]);
  const mostUrgent = expiring[0];

  // The bag the user chose as "in the container" (canisterBean reads
  // preferences.last_bean_id), falling back to the first active bag. Using the
  // same source as the Coffee screen and Morning Mode keeps them in sync.
  const activeBag = canisterBean(store.beans, store.preferences) || findActiveBags(store.beans)[0];
  const forecast = activeBag ? forecastDepletion(activeBag, store.brewLogs) : null;
  const previewMode = brewSugg?.suggested_mode === "cold" ? "cold" : brewSugg?.suggested_mode === "iced" ? "iced" : "hot";
  const previewBrew = useMemo(
    () => (activeBag ? computeMorningBrew({
      bean: activeBag,
      cups: store.preferences?.default_cups || 8,
      mode: previewMode,
      tasteProfile: store.tasteProfile,
      defaultProfileId: store.preferences?.default_brew_profile,
      grinder: store.gear?.grinder,
    }) : null),
    [activeBag, previewMode, store.tasteProfile, store.preferences?.default_brew_profile, store.preferences?.default_cups, store.gear?.grinder]
  );

  const shoppingPending = (store.shoppingList || []).filter(i => !i.done).length;

  const tonightSugg = useMemo(() => {
    const recs = store.recipes || [];
    if (recs.length === 0) return null;
    const expiringNames = new Set(expiring.map(e => String(e.sku?.canonical_name || "").toLowerCase()).filter(Boolean));
    return recs.map(r => {
      const ings = r.ingredients || [];
      const matches = ings.filter(i => [...expiringNames].some(en => String(i.name || "").toLowerCase().includes(en))).length;
      return { recipe: r, matches };
    }).sort((a, b) => b.matches - a.matches || (b.recipe.times_made || 0) - (a.recipe.times_made || 0))[0]?.recipe || null;
  }, [store.recipes, expiring]);

  const urgentName = mostUrgent?.sku?.canonical_name;
  // Prototype brew line: "30g · Ode 5 · 205°" (no °F unit).
  const brewLine = previewBrew ? `${previewBrew.dose_g}g · Ode ${previewBrew.grind.setting} · ${previewBrew.temp_f}°` : null;

  // "Yesterday's cup" bean for the AI-first rate card — the most recent brew log's
  // bean, falling back to the open bag. null ⇒ no prior brew to rate.
  const beansById = useMemo(() => new Map((store.beans || []).map(b => [b.id, b])), [store.beans]);
  const recentBrew = (store.brewLogs || [])[0];
  const yesterdayBeanName = (recentBrew ? beansById.get(recentBrew.bean_id) : null)?.name || activeBag?.name || null;

  const podcastFeeds = store.preferences?.podcast_feeds || [];

  // Metrics reused by hybrid/traditional grids.
  const metricList = [
    {
      key: "coffee", label: "Caffè",
      value: activeBag ? activeBag.name : "No bag open",
      detail: activeBag ? (brewLine || (forecast ? `~${forecast.brews_remaining} brews left` : "Tap to dial in")) : "Add a bag to start",
      onClick: () => onNavigate("coffee"),
    },
    {
      key: "use", label: "Use",
      value: urgentName || "All fresh",
      detail: mostUrgent
        ? (mostUrgent.days_until_expiry < 0 ? "expired" : mostUrgent.days_until_expiry === 0 ? "today" : mostUrgent.days_until_expiry === 1 ? "tomorrow" : `${mostUrgent.days_until_expiry} days left`)
        : "Nothing expiring soon",
      warn: !!mostUrgent, onClick: () => onNavigate("pantry"),
    },
    {
      key: "tonight", label: "Tonight",
      value: tonightSugg ? tonightSugg.title : "Open",
      detail: tonightSugg ? (tonightSugg.total_min ? `${tonightSugg.total_min} min` : "ready") : "Pick a mood",
      onClick: () => onNavigate("tonight"),
    },
    {
      key: "spesa", label: "Shopping",
      value: shoppingPending > 0 ? `${shoppingPending} item${shoppingPending !== 1 ? "s" : ""}` : "Empty",
      detail: shoppingPending > 0 ? "to buy" : "list clear",
      onClick: () => onNavigate("shopping"),
    },
  ];

  // Adaptive: order the cards by time-of-day, refined by personal usage.
  const metrics = rankMetrics(usage, phase, hour)
    .map(k => metricList.find(m => m.key === k))
    .filter(Boolean);

  // The "Oggi:" strip summary (hybrid) — real data, prototype format.
  const oggiText = [
    activeBag && `brew ${activeBag.name}`,
    tonightSugg && tonightSugg.title,
    shoppingPending > 0 && `${shoppingPending} to buy`,
  ].filter(Boolean).join(" · ") || "nothing planned yet";

  const isAI = viewMode === "ai" || viewMode === "adaptive";
  const goMorning = () => { window.location.hash = "morning"; };
  const morningDone = !!store.morningSession?.completed_at;

  return (
    <div className="pb-8">
      <WeatherHeader weather={weather} greeting={greeting} dayName={dayName} />

      {viewMode === "adaptive" && (
        <div className="font-sf text-[11px] font-semibold text-pronto-text-3 mb-3">
          ◷ Adaptive · showing your {PHASE_LABEL[phase] || "morning"} view
        </div>
      )}

      {isAI && (
        <div className="space-y-[14px]">
          <GlassCard radius={24} elevated className="p-[18px]">
            <div className="font-sf text-[11px] font-bold tracking-[0.05em] text-pronto-navy">◇ PRONTO SUGGESTS</div>
            {yesterdayBeanName ? (
              <>
                <p className="font-sf text-[16px] leading-[1.45] text-pronto-ink mt-2">
                  First — how was yesterday&apos;s <strong className="font-semibold">{yesterdayBeanName}</strong>?
                </p>
                <RateButtons beanName={yesterdayBeanName} />
              </>
            ) : (
              <p className="font-sf text-[16px] leading-[1.45] text-pronto-ink mt-2">
                Add a bag and Pronto will plan your morning.
              </p>
            )}
            <button
              onClick={goMorning}
              aria-label={morningDone ? "Morning complete — review your routine" : "Start Routine"}
              className="w-full h-11 rounded-[14px] bg-[rgba(63,74,99,0.08)] text-pronto-navy font-sf text-[15px] font-semibold flex items-center justify-center mt-4"
            >
              {morningDone ? "Morning complete ☕" : "Start Routine →"}
            </button>
          </GlassCard>


          {podcastFeeds.length > 0 && <BriefingCard feeds={podcastFeeds} onOpen={() => onNavigate("listen")} />}
        </div>
      )}

      {viewMode === "hybrid" && (
        <div className="space-y-[14px]">
          <GlassCard radius={16} className="px-[15px] py-[13px] flex items-center gap-[10px]">
            <span className="text-pronto-navy" aria-hidden="true">◇</span>
            <div className="flex-1 font-sf text-[13.5px] leading-[1.35] text-pronto-ink">
              <span className="font-semibold">Oggi:</span> {oggiText}
            </div>
            <button onClick={() => onNavigate("coffee")} aria-label="Go to coffee" className="font-sf text-[13px] font-semibold text-pronto-navy">Go</button>
          </GlassCard>
          <MetricGrid metrics={metrics} variant="hybrid" brewLine={brewLine} onNavigate={onNavigate} />
          <MorningEntryCard session={store.morningSession} onOpen={goMorning} />
        </div>
      )}

      {viewMode === "traditional" && (
        <div className="space-y-[12px]">
          <MetricGrid metrics={metrics} variant="traditional" onNavigate={onNavigate} />
          <MorningEntryCard session={store.morningSession} onOpen={goMorning} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Weather header — sun/cloud (real condition emoji) + `.rnd` 32px "{temp}° & {word}"
// title, droplet sub-line "Feels like N° · N% rain · Day". The visible greeting IS
// the weather line (spec note); the localized greeting is wired into the header's
// accessible name so it's still announced. Right padding clears the meds pill.
function conditionWord(t) {
  return t < 40 ? "cold" : t < 60 ? "crisp" : t < 75 ? "pleasant" : t < 85 ? "warm" : "hot";
}

function WeatherHeader({ weather, greeting, dayName }) {
  const temp = weather?.temp_f;
  const feels = weather?.feels_f;
  const precip = weather?.peak_3h_precip_pct;
  const cond = temp != null ? conditionWord(temp) : null;
  const emoji = weather?.emoji || "☀️";
  const aria = (weather && temp != null)
    ? `${greeting}. ${temp} degrees and ${cond}${feels != null ? `, feels like ${feels}` : ""}${precip != null ? `, ${precip}% chance of rain` : ""}, ${dayName}`
    : `${greeting}, ${dayName}`;
  return (
    <header aria-label={aria}>
      <div className="flex items-center gap-[11px] mt-1.5 mb-0.5 pr-[84px]">
        <span className="text-[30px] leading-none" aria-hidden="true">{emoji}</span>
        <h1 className="font-round font-bold text-[32px] leading-none tracking-[-0.02em] text-pronto-ink whitespace-nowrap">
          {temp != null ? (
            <>{temp}°{cond && <span className="text-[19px] font-semibold text-pronto-text-2"> &amp; {cond}</span>}</>
          ) : greeting}
        </h1>
      </div>
      <div className="flex items-center gap-1.5 font-sf text-[13.5px] text-pronto-text-2 mb-4" aria-hidden="true">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="#6E8CB8" className="flex-shrink-0">
          <path d="M12 2.7S5.8 10 5.8 15a6.2 6.2 0 0 0 12.4 0C18.2 10 12 2.7 12 2.7z" />
        </svg>
        {(weather && feels != null)
          ? `Feels like ${feels}° · ${precip}% rain · ${dayName}`
          : dayName}
      </div>
    </header>
  );
}

// AI-first rate-yesterday buttons. Local-only (mirrors the prototype's rateUp/
// rateDown mock — no DB write, so the taste-learning loop is untouched); the
// proper rating flow lives in the +40min RatingSheet / Morning Mode Tweak plan.
function RateButtons() {
  const [rated, setRated] = useState(null); // "up" | "down" | null
  const note = rated === "up"
    ? "Perfetto — this morning repeats this cup."
    : rated === "down"
      ? "Got it — we'll adjust this morning's recipe (opening the Tweak plan)."
      : null;
  const base = "flex-1 h-10 rounded-[12px] font-sf text-[15px] font-semibold border-[1.5px] flex items-center justify-center gap-1.5 transition-colors motion-reduce:transition-none";
  const state = (on, kind) => {
    if (!on) return "bg-[var(--pg-7)] text-pronto-ink border-transparent";
    return kind === "up"
      ? "bg-[rgba(46,158,143,0.14)] text-[#1C7268] border-[#1C7268]"
      : "bg-[rgba(192,85,47,0.12)] text-pronto-warn border-pronto-warn";
  };
  return (
    <>
      <div className="flex gap-2 mt-[11px]">
        <button aria-label="Yesterday's cup was good" aria-pressed={rated === "up"} onClick={() => setRated("up")} className={`${base} ${state(rated === "up", "up")}`}>👍 Good</button>
        <button aria-label="Yesterday's cup was off" aria-pressed={rated === "down"} onClick={() => setRated("down")} className={`${base} ${state(rated === "down", "down")}`}>👎 Off</button>
      </div>
      {note && <div className="font-sf text-[12.5px] font-semibold text-[#1C7268] mt-2.5">{note}</div>}
    </>
  );
}

// Morning briefing card (AI-first) — expanded by default: the queue is the point
// of the card, so the feed list is visible without a tap. Wired to the real
// podcast subscriptions (preferences.podcast_feeds). Per-episode titles/durations
// need the feed proxy and stay on Listen; tapping any row goes there.
function BriefingCard({ feeds, onOpen }) {
  const n = feeds.length;
  const shown = feeds.slice(0, 4);
  const rest = n - shown.length;
  return (
    <GlassCard radius={20} className="px-[14px] py-[13px]">
      <div className="flex items-center gap-[13px]">
        <PlayEverywhereButton feeds={feeds} />
        <div className="flex-1 min-w-0">
          <div className="font-sf text-[10px] font-bold uppercase tracking-[0.05em] text-pronto-text-3">Morning briefing</div>
          <div className="font-round font-bold text-[16px] text-pronto-ink">{n} feed{n !== 1 ? "s" : ""}</div>
        </div>
        <button onClick={onOpen} aria-label="Open the briefing in Listen" className="flex-shrink-0 p-1 -mr-1">
          <ChevronRight size={18} className="text-pronto-text-3" />
        </button>
      </div>

      <ul className="mt-3 border-t border-[var(--p-sep)] pt-1">
        {shown.map((f, i) => (
          <li key={f.id || f.url || i}>
            <button
              onClick={onOpen}
              className="w-full flex items-center gap-2.5 py-[7px] text-left"
              aria-label={`${f.title || "Feed"} — open in Listen`}
            >
              <span className="w-7 h-7 rounded-[7px] bg-[rgba(63,74,99,0.10)] grid place-items-center flex-shrink-0
                               font-sf text-[11px] font-bold text-pronto-navy tabular">{i + 1}</span>
              <span className="flex-1 min-w-0 font-sf text-[13.5px] text-pronto-ink truncate">{f.title || "Untitled feed"}</span>
            </button>
          </li>
        ))}
        {rest > 0 && (
          <li>
            <button onClick={onOpen} className="w-full text-left py-[7px] font-sf text-[12.5px] text-pronto-text-2">
              +{rest} more in Listen
            </button>
          </li>
        )}
      </ul>
    </GlassCard>
  );
}

// "Play everywhere" — groups every Sonos room, then streams the briefing to the
// bonded group. Confirms first (and names the rooms) because bonding speakers
// changes what's playing in the whole house, not just here.
function PlayEverywhereButton({ feeds }) {
  const [rooms, setRooms] = useState(null);   // null = not looked up yet
  const [confirm, setConfirm] = useState(false);
  const [state, setState] = useState("idle"); // idle | working | done | error
  const [msg, setMsg] = useState("");

  const open = async () => {
    setConfirm(true);
    if (rooms === null) {
      try {
        const players = await listPlayers();
        setRooms(players.map(p => p.name));
      } catch (e) {
        // Show the reason rather than an empty room list, which reads as
        // "you own no speakers" when it usually means the request failed.
        setRooms([]);
        setMsg(e?.message || "Couldn't reach Sonos");
        setState("error");
      }
    }
  };

  const play = async () => {
    setState("working");
    try {
      const url = feeds[0]?.url;
      if (!url) throw new Error("No feed to play");
      const { playerCount } = await playEverywhere(url, "Morning briefing");
      setState("done");
      setMsg(`Playing in ${playerCount} room${playerCount !== 1 ? "s" : ""}`);
      setConfirm(false);
    } catch (e) {
      setState("error");
      setMsg(e.message || "Couldn't reach Sonos");
    }
  };

  const roomLine = rooms === null ? "Finding your speakers…"
    : rooms.length === 0 ? "No Sonos speakers found on this account."
    : rooms.length <= 3 ? rooms.join(", ")
    : `${rooms.slice(0, 2).join(", ")} + ${rooms.length - 2} more`;

  return (
    <>
      <button
        onClick={open}
        aria-label="Play the morning briefing in every room"
        className="w-12 h-12 rounded-full bg-pronto-accent grid place-items-center text-white text-[18px] flex-shrink-0"
      >
        ▶
      </button>

      <Sheet open={confirm} onClose={() => setConfirm(false)}>
        <div className="p-5">
          <div className="font-round font-bold text-[19px] text-pronto-ink">Play in every room?</div>
          <p className="font-sf text-[13.5px] leading-[1.5] text-pronto-text-2 mt-1.5">
            Pronto will group all your Sonos speakers together and start the briefing on them.
          </p>
          <div className="mt-3 rounded-[12px] bg-[rgba(63,74,99,0.06)] px-3.5 py-2.5">
            <div className="font-sf text-[10.5px] font-bold uppercase tracking-[0.05em] text-pronto-text-3">Rooms</div>
            <div className="font-sf text-[13.5px] text-pronto-ink mt-0.5">{roomLine}</div>
          </div>
          {state === "error" && (
            <div className="font-sf text-[12.5px] text-pronto-warn mt-2.5">{msg}</div>
          )}
          <div className="flex gap-2.5 mt-4">
            <button
              onClick={() => setConfirm(false)}
              className="flex-1 h-11 rounded-[13px] border border-[var(--p-sep)] font-sf text-[15px] font-semibold text-pronto-ink"
            >
              Cancel
            </button>
            <button
              onClick={play}
              disabled={state === "working" || rooms?.length === 0}
              className="flex-1 h-11 rounded-[13px] bg-pronto-accent text-white font-sf text-[15px] font-semibold disabled:opacity-50"
            >
              {state === "working" ? "Grouping…" : "Play everywhere"}
            </button>
          </div>
        </div>
      </Sheet>
    </>
  );
}

// Traditional cells show the value alone, so long strings (recipe titles, bean
// names) used to clip at one line. Two lines plus a step down in size at length
// buys back most of them without the 2×2 grid losing its even rhythm.
function traditionalValueClass(value) {
  const len = String(value ?? "").length;
  if (len > 34) return "text-[15px]";
  if (len > 22) return "text-[17px]";
  return "text-[19px]";
}

// 2×2 action grid. hybrid = detail line under each value + a special Caffè cell
// (Change / Rate-yesterday links); traditional = value only, two-line clamp with
// a shared row height (min 86px, both cards in a row stretch to the taller one).
function MetricGrid({ metrics, variant, brewLine, onNavigate }) {
  const hybrid = variant === "hybrid";
  return (
    <div className="grid grid-cols-2 gap-[10px] items-stretch">
      {metrics.map(m => {
        if (hybrid && m.key === "coffee") return <CoffeeCell key={m.key} m={m} brewLine={brewLine} onNavigate={onNavigate} />;
        return (
          <button
            key={m.key}
            onClick={m.onClick}
            aria-label={`${m.label}: ${m.value}${hybrid ? `, ${m.detail}` : ""}`}
            className={`text-left block ${hybrid ? "" : "h-full"}`}
          >
            <GlassCard radius={16} className={hybrid ? "p-[14px]" : "p-[16px] min-h-[86px] h-full flex flex-col"}>
              <div className="font-sf text-[10px] font-bold uppercase tracking-[0.02em] text-pronto-text-3">{m.label}</div>
              {hybrid ? (
                <>
                  <div className="font-round font-bold text-[19px] leading-tight text-pronto-ink truncate mt-1.5">{m.value}</div>
                  <div className={`font-sf text-[12px] mt-0.5 truncate ${m.warn ? "text-pronto-warn" : "text-pronto-text-2"}`}>{m.detail}</div>
                </>
              ) : (
                <div className={`font-round font-bold leading-[1.15] text-pronto-ink line-clamp-2 mt-2 ${traditionalValueClass(m.value)}`}>
                  {m.value}
                </div>
              )}
            </GlassCard>
          </button>
        );
      })}
    </div>
  );
}

// Hybrid Caffè cell — open-bag readout with "Change" + "Rate yesterday's cup ›"
// affordances. One button (all routes lead to Coffee); the two labels are visual.
function CoffeeCell({ m, brewLine, onNavigate }) {
  return (
    <button
      onClick={() => onNavigate("coffee")}
      aria-label={`Coffee, ${m.value}${brewLine ? `, ${brewLine}` : ""}. Change or rate yesterday's cup`}
      className="text-left block"
    >
      <GlassCard radius={16} className="p-[14px]">
        <div className="flex items-center justify-between gap-2">
          <span className="font-sf text-[10px] font-bold uppercase tracking-[0.02em] text-pronto-text-3 truncate">Caffè · open bag</span>
          <span className="font-sf text-[11px] font-semibold text-pronto-navy flex-shrink-0">Change</span>
        </div>
        <div className="font-round font-bold text-[19px] leading-tight text-pronto-ink truncate mt-1.5">{m.value}</div>
        <div className="font-sf text-[12px] text-pronto-text-2 truncate">{brewLine || m.detail}</div>
        <div className="font-sf text-[11.5px] font-semibold text-[#1C7268] mt-1.5">Rate yesterday&apos;s cup ›</div>
      </GlassCard>
    </button>
  );
}

// Entry point into the guided Morning Mode flow. Kept for hybrid/traditional so
// Morning stays reachable there (AI-first embeds "Start Morning Mode" in the
// suggests card). Shows a "done" state once today's session is complete.
function MorningEntryCard({ session, onOpen }) {
  const done = !!session?.completed_at;
  return (
    <button onClick={onOpen} className="w-full text-left block">
      <GlassCard radius={16} className="p-3.5 flex items-center gap-3">
        <span className="text-[20px] leading-none" aria-hidden="true">☕️</span>
        <div className="flex-1 min-w-0">
          <div className="font-sf text-[14.5px] font-semibold text-pronto-ink">
            {done ? "Morning complete" : "Start Routine"}
          </div>
          <div className="font-sf text-[12px] text-pronto-text-3">
            {done ? "Perfetto ☕ — tap to review" : "Meds timer, prep, and today's brew"}
          </div>
        </div>
        <ChevronRight size={18} className="text-pronto-text-3" />
      </GlassCard>
    </button>
  );
}
